import { useCallback, useEffect, useState } from "react";
import { Clock, Inbox, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Panel } from "@/components/admin/Field";
import { formatDate } from "@/lib/format";

const STATUS = {
  new: { label: "Nova", cls: "bg-eden-orange/15 text-eden-orange" },
  reviewing: { label: "Em análise", cls: "bg-sky-500/15 text-sky-300" },
  accepted: { label: "Aceite", cls: "bg-emerald-500/15 text-emerald-300" },
  rejected: { label: "Recusada", cls: "bg-red-500/15 text-red-300" },
};

function ApplicationCard({ app, onChange }) {
  const [notes, setNotes] = useState(app.notes || "");
  const [open, setOpen] = useState(app.status === "new");
  const save = async (status) => {
    try {
      await api.patch(`/admin/applications/${app.id}`, { status, notes });
      toast.success("Candidatura atualizada.");
      onChange();
    } catch (e) {
      toast.error(errorMessage(e, "Não foi possível atualizar."));
    }
  };
  const st = STATUS[app.status] || STATUS.new;
  return (
    <li className="rounded-md border border-eden-border bg-eden-bg" data-testid={`application-${app.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full flex-wrap items-center gap-3 p-4 text-left" aria-expanded={open} data-testid={`application-toggle-${app.id}`}>
        <span className={`rounded-sm px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${st.cls}`} data-testid={`application-status-${app.id}`}>{st.label}</span>
        <span className="font-display text-base font-semibold text-eden-text">{app.name}</span>
        <span className="text-sm text-eden-muted">{app.age} anos · @{app.discord}{app.role_wanted ? ` · ${app.role_wanted}` : ""}</span>
        <span className="ml-auto inline-flex items-center gap-1 font-mono text-xs text-eden-muted"><Clock size={12} /> {formatDate(app.created_at)}</span>
      </button>
      {open && (
        <div className="space-y-4 border-t border-eden-border p-4">
          <dl className="grid gap-4 sm:grid-cols-2">
            <div><dt className="font-mono text-[11px] uppercase tracking-widest text-eden-muted">Disponibilidade</dt><dd className="mt-1 text-sm text-eden-text">{app.availability}</dd></div>
            {app.experience && <div><dt className="font-mono text-[11px] uppercase tracking-widest text-eden-muted">Experiência</dt><dd className="mt-1 whitespace-pre-line text-sm text-eden-text">{app.experience}</dd></div>}
            <div className="sm:col-span-2"><dt className="font-mono text-[11px] uppercase tracking-widest text-eden-muted">Motivação</dt><dd className="mt-1 whitespace-pre-line text-sm text-eden-text">{app.motivation}</dd></div>
          </dl>
          <div>
            <label htmlFor={`notes-${app.id}`} className="flex items-center gap-1.5 text-sm font-medium text-eden-text"><MessageSquare size={14} /> Notas internas</label>
            <textarea id={`notes-${app.id}`} value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={1000} className="input-eden mt-1.5 min-h-[60px]" placeholder="Só a equipa vê isto." data-testid={`application-notes-${app.id}`} />
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(STATUS).map(([key, s]) => (
              <button key={key} type="button" onClick={() => save(key)} className={`h-9 rounded-sm border px-3 text-xs font-semibold uppercase tracking-wider transition-colors ${app.status === key ? "border-eden-orange text-eden-text" : "border-eden-border text-eden-secondary hover:border-eden-secondary hover:text-eden-text"}`} data-testid={`application-set-${key}-${app.id}`}>{s.label}</button>
            ))}
            {app.reviewed_by && <span className="ml-auto self-center text-xs text-eden-muted">Revisto por {app.reviewed_by}</span>}
          </div>
        </div>
      )}
    </li>
  );
}

export default function EditCandidaturas() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const load = useCallback(() => api.get(`/admin/applications${filter ? `?status=${filter}` : ""}`).then(({ data: d }) => setData(d)).catch(() => toast.error("Não foi possível carregar as candidaturas.")), [filter]);
  useEffect(() => { load(); }, [load]);
  const counts = data?.counts || {};
  return (
    <Panel title="Candidaturas à staff" description="Respostas enviadas pelo formulário do site. Muda o estado e deixa notas para a equipa; cada mudança fica no registo de alterações.">
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filtrar por estado">
        <button type="button" role="tab" aria-selected={filter === ""} onClick={() => setFilter("")} className={`h-9 rounded-sm border px-3 text-xs font-semibold uppercase tracking-wider ${filter === "" ? "border-eden-orange text-eden-text" : "border-eden-border text-eden-secondary"}`} data-testid="applications-filter-all">Todas</button>
        {Object.entries(STATUS).map(([key, s]) => (
          <button key={key} type="button" role="tab" aria-selected={filter === key} onClick={() => setFilter(key)} className={`h-9 rounded-sm border px-3 text-xs font-semibold uppercase tracking-wider ${filter === key ? "border-eden-orange text-eden-text" : "border-eden-border text-eden-secondary"}`} data-testid={`applications-filter-${key}`}>{s.label} <span className="ml-1 font-mono text-eden-muted">{counts[key] ?? 0}</span></button>
        ))}
      </div>
      {!data && <p className="text-sm text-eden-muted">A carregar…</p>}
      {data && data.items.length === 0 && (
        <div className="flex flex-col items-center gap-2 py-12 text-center text-eden-muted" data-testid="applications-empty"><Inbox size={28} /><p className="text-sm">Ainda não há candidaturas{filter ? " neste estado" : ""}.</p></div>
      )}
      {data && data.items.length > 0 && <ul className="space-y-3" data-testid="applications-list">{data.items.map((a) => <ApplicationCard key={a.id} app={a} onChange={load} />)}</ul>}
    </Panel>
  );
}
